import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield, X, Smartphone, RefreshCw, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { api } from '../services/api';
import { toast } from 'sonner';

// Composant pour entrer le code 2FA lors de la connexion
export function TOTPLoginVerification({ customerId, customerName, onSuccess, onCancel }) {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const inputRefs = useRef([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (index, value) => {
    if (!/^[0-9]*$/.test(value)) return;
    if (value.length > 1) value = value.slice(-1);
    
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    // Auto-focus suivant
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit quand tous les chiffres sont entrés
    if (newCode.every(d => d !== '') && index === 5) {
      handleVerify(newCode.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text').trim();
    if (/^\d{6}$/.test(pasteData)) {
      const newCode = pasteData.split('');
      setCode(newCode);
      inputRefs.current[5]?.focus();
      setTimeout(() => handleVerify(pasteData), 100);
    }
  };

  const handleVerify = async (codeString) => {
    setIsLoading(true);
    setError('');
    try {
      const result = await api.validateTOTP(customerId, codeString);
      if (result.success) {
        toast.success('Connexion réussie !');
        onSuccess(result.customer);
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Code incorrect';
      setError(message);
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="w-full max-w-md backdrop-blur-xl rounded-3xl p-6 border-2"
        style={{
          background: 'rgba(10, 0, 30, 0.95)',
          borderColor: '#00FFFF',
          boxShadow: '0 0 40px rgba(0, 255, 255, 0.3)'
        }}
      >
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-full bg-cyan-500/20 flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-cyan-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Double Authentification</h2>
          <p className="text-gray-400 text-sm">
            Entrez le code de votre application<br/>
            <span className="text-cyan-400 font-semibold">Google Authenticator</span>
          </p>
          {customerName && (
            <p className="text-cyan-300 mt-2 text-sm">👋 {customerName}</p>
          )}
        </div>

        {/* Code Input */}
        <div className="flex justify-center gap-2 mb-6" onPaste={handlePaste}>
          {code.map((digit, index) => (
            <React.Fragment key={index}>
              {index === 3 && <div className="w-3" />}
              <input
                ref={el => inputRefs.current[index] = el}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className={`w-12 h-14 text-center text-2xl font-bold rounded-xl border-2 transition-all focus:outline-none focus:ring-2 focus:ring-cyan-500/50 ${
                  error 
                    ? 'border-red-500 bg-red-500/10 text-red-400' 
                    : 'border-cyan-500/50 bg-black/50 text-white'
                }`}
                disabled={isLoading}
              />
            </React.Fragment>
          ))}
        </div>

        {/* Error Message */}
        {error && (
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-red-400 text-center text-sm mb-4"
          >
            ❌ {error}
          </motion.p>
        )}

        {/* Info */}
        <div className="bg-black/30 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3 text-sm text-gray-400">
            <Smartphone className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="mb-1">Ouvrez <span className="text-cyan-400 font-semibold">Google Authenticator</span> sur votre téléphone</p>
              <p>Le code change toutes les 30 secondes</p>
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-3">
          <Button
            onClick={onCancel}
            variant="outline"
            className="flex-1 border-gray-500 text-gray-300"
            disabled={isLoading}
          >
            Annuler
          </Button>
          <Button
            onClick={() => handleVerify(code.join(''))}
            disabled={isLoading || code.some(d => d === '')}
            className="flex-1 font-bold"
            style={{
              background: 'linear-gradient(135deg, #00FFFF 0%, #0066FF 100%)',
              color: '#000'
            }}
          >
            {isLoading ? (
              <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Vérification...</>
            ) : (
              'Vérifier'
            )}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Composant pour configurer le 2FA (affiche le QR code)
export function TOTPSetupModal({ customerId, onSuccess, onClose }) {
  const [step, setStep] = useState('loading'); // loading, qrcode, verify, success
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const inputRefs = useRef([]);

  useEffect(() => {
    initSetup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const initSetup = async () => {
    setStep('loading');
    try {
      const data = await api.setupTOTP(customerId);
      setQrCodeUrl(data.qrCodeUrl);
      setSecret(data.secret);
      setStep('qrcode');
    } catch (err) {
      toast.error('Erreur lors de l\'initialisation du 2FA');
      onClose();
    }
  };

  const handleChange = (index, value) => {
    if (!/^[0-9]*$/.test(value)) return;
    if (value.length > 1) value = value.slice(-1);
    
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    if (newCode.every(d => d !== '') && index === 5) {
      handleVerify(newCode.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async (codeString) => {
    setIsLoading(true);
    setError('');
    try {
      await api.verifyTOTP(customerId, codeString);
      setStep('success');
      toast.success('2FA activé avec succès !');
    } catch (err) {
      const message = err.response?.data?.detail || 'Code incorrect';
      setError(message);
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(secret);
    toast.success('Secret copié !');
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="w-full max-w-md backdrop-blur-xl rounded-3xl p-6 border-2 my-8"
        style={{
          background: 'rgba(10, 0, 30, 0.95)',
          borderColor: '#00FF00',
          boxShadow: '0 0 40px rgba(0, 255, 0, 0.3)'
        }}
      >
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white"
        >
          <X className="w-6 h-6" />
        </button>

        {step === 'loading' && (
          <div className="text-center py-12">
            <RefreshCw className="w-12 h-12 text-green-400 animate-spin mx-auto mb-4" />
            <p className="text-gray-400">Génération du QR code...</p>
          </div>
        )}

        {step === 'qrcode' && (
          <>
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Activer le 2FA</h2>
              <p className="text-gray-400 text-sm">
                Scannez ce QR code avec<br/>Google Authenticator
              </p>
            </div>

            {/* QR Code */}
            <div className="flex justify-center mb-6">
              <div className="bg-white p-4 rounded-2xl">
                <img src={qrCodeUrl} alt="QR Code 2FA" className="w-48 h-48" />
              </div>
            </div>

            {/* Secret manuel */}
            <div className="mb-4">
              <p className="text-xs text-gray-500 text-center mb-2">Ou entrez ce code manuellement :</p>
              <div 
                onClick={copySecret}
                className="bg-black/50 border border-green-500/50 rounded-xl p-3 cursor-pointer hover:bg-green-500/10 transition-colors"
              >
                <code className="text-green-400 text-sm font-mono break-all block text-center">{secret}</code>
                <p className="text-xs text-gray-500 text-center mt-2">Cliquez pour copier</p>
              </div>
            </div>
            
            {/* Avertissement clé de secours */}
            <div className="mb-6 p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/30">
              <p className="text-yellow-400 text-xs font-medium text-center">
                ⚠️ <strong>Important :</strong> Notez ce code secret quelque part en sécurité !
              </p>
              <p className="text-yellow-400/70 text-xs text-center mt-1">
                Si vous perdez votre téléphone, ce code vous permettra de récupérer l&apos;accès.
              </p>
            </div>

            <Button
              onClick={() => {
                setStep('verify');
                setTimeout(() => inputRefs.current[0]?.focus(), 100);
              }}
              className="w-full font-bold"
              style={{
                background: 'linear-gradient(135deg, #00FF00 0%, #00AA00 100%)',
                color: '#000'
              }}
            >
              J&apos;ai scanné le QR code
            </Button>
          </>
        )}

        {step === 'verify' && (
          <>
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-white mb-2">Vérification</h2>
              <p className="text-gray-400 text-sm">
                Entrez le code à 6 chiffres affiché<br/>dans Google Authenticator
              </p>
            </div>

            {/* Code Input */}
            <div className="flex justify-center gap-2 mb-6">
              {code.map((digit, index) => (
                <React.Fragment key={index}>
                  {index === 3 && <div className="w-3" />}
                  <input
                    ref={el => inputRefs.current[index] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className={`w-12 h-14 text-center text-2xl font-bold rounded-xl border-2 transition-all focus:outline-none focus:ring-2 focus:ring-green-500/50 ${
                      error 
                        ? 'border-red-500 bg-red-500/10 text-red-400' 
                        : 'border-green-500/50 bg-black/50 text-white'
                    }`}
                    disabled={isLoading}
                  />
                </React.Fragment>
              ))}
            </div>

            {error && (
              <p className="text-red-400 text-center text-sm mb-4">❌ {error}</p>
            )}

            <div className="flex gap-3">
              <Button
                onClick={() => setStep('qrcode')}
                variant="outline"
                className="flex-1 border-gray-500 text-gray-300"
              >
                Retour
              </Button>
              <Button
                onClick={() => handleVerify(code.join(''))}
                disabled={isLoading || code.some(d => d === '')}
                className="flex-1 font-bold"
                style={{
                  background: 'linear-gradient(135deg, #00FF00 0%, #00AA00 100%)',
                  color: '#000'
                }}
              >
                {isLoading ? 'Vérification...' : 'Activer'}
              </Button>
            </div>
          </>
        )}

        {step === 'success' && (
          <div className="text-center py-8">
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">2FA Activé !</h2>
            <p className="text-gray-400 text-sm mb-6">
              Votre compte est maintenant protégé par<br/>la double authentification
            </p>
            <Button
              onClick={() => {
                onSuccess();
                onClose();
              }}
              className="w-full font-bold"
              style={{
                background: 'linear-gradient(135deg, #00FF00 0%, #00AA00 100%)',
                color: '#000'
              }}
            >
              Terminé
            </Button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

export default TOTPLoginVerification;
